export { DriverWorkspace } from "./driver-workspace";
export { TaxiVoiceProvider, TaxiCallButton, RestaurantCallButton } from "./voice-calls";
import { TaxiCallButton } from "./voice-calls";
import { useDetailRecord } from "@taunggyi-plus/auth";
import { formatRecordDate } from "@taunggyi-plus/design-system";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { AssignedTaxiOrders } from "./driver-orders";
import { submitOrderRequest } from "@taunggyi-plus/api-client";
import { useOrderRefresh } from "@taunggyi-plus/auth";
import { ActionIcon } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { ProviderReputation, ProviderSaveButton } from "@taunggyi-plus/finance-feature";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  RecordTable,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";
type Group = {
  id: string;
  organization_id: string;
  display_name: string;
  address: string;
  primary_phone: string;
  secondary_phone: string | null;
  service_note: string;
  is_published: boolean;
};
type Driver = {
  id: string;
  user_id?: string | null;
  group_id: string;
  display_name: string;
  phone: string;
  vehicle_type: string;
  vehicle_number: string;
  vehicle_color: string;
  is_available: boolean;
  is_verified: boolean;
  nrc?: string;
  photo_data_url?: string | null;
};
type DriverProfile = {
  driver_id: string;
  group_id: string;
  nrc: string;
};
type TaxiOrderStatus =
  "pending" | "confirmed" | "assigned" | "in_progress" | "completed" | "cancelled" | "rejected";
type TaxiOrder = {
  id: string;
  group_id: string;
  customer_user_id: string | null;
  driver_id: string | null;
  source: "customer" | "manual";
  cancellation_reason: string;
  status: TaxiOrderStatus;
  customer_name: string;
  customer_phone: string;
  pickup_area: string;
  dropoff_area: string;
  requested_pickup_at: string | null;
  fare_minor: number;
  note: string;
  created_by: string;
  created_at: string;
};

const rideFareNotice: Record<Locale, string> = {
  en: "Please ask the driver to confirm the fare before starting your ride.",
  my: "ခရီးမစတင်မီ ယာဉ်စီးခကို ယာဉ်မောင်းနှင့် မေးမြန်းအတည်ပြုပါ။",
  shn: "Please ask the driver to confirm the fare before starting your ride.",
  zh: "出发前，请向司机确认车费。",
};

const taxiOperationsEnglish = {
  drivers: "Drivers",
  driversHelp: "Manage driver identity, verification details, vehicles, and availability.",
  nrc: "NRC",
  searchDrivers: "Search by driver name or NRC",
  allDrivers: "All drivers",
  edit: "Edit",
  delete: "Delete",
  cancel: "Cancel",
  saveChanges: "Save changes",
  driverAdded: "Driver added for Admin verification.",
  driverUpdated: "Driver updated.",
  driverDeleted: "Driver deleted.",
  dashboardSummary: "Live ride operations at a glance",
  pendingOrders: "Pending orders",
  assignedOrders: "Assigned",
  activeOrders: "In progress",
  completedOrders: "Completed",
  recentOrders: "Recent customer orders",
  recentRecords: "Recent records",
  seeMore: "See more",
  noOrders: "No ride orders yet.",
  orders: "Orders",
  ordersHelp: "Manage customer ride orders and manual taxi records.",
  addRecord: "Add record",
  updateRecord: "Update record",
  customerName: "Customer name",
  customerPhone: "Customer phone",
  requestedPickup: "Requested pickup",
  status: "Status",
  source: "Source",
  allSources: "All sources",
  customerOrder: "Customer order",
  manualRecord: "Manual record",
  allDriversFilter: "All drivers",
  unassigned: "Unassigned",
  allStatuses: "All statuses",
  searchOrders: "Search customer, phone, pickup, or destination",
  orderAdded: "Record added.",
  orderUpdated: "Record updated.",
  orderDeleted: "Record deleted.",
  requestRide: "Send ride order",
  requestRideHelp: "Send your pickup request directly to this driver.",
  sendOrder: "Send order",
  orderSent: "Your ride order was sent.",
  pending: "Pending",
  confirmed: "Confirmed",
  assigned: "Assigned",
  inProgress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  rejected: "Rejected",
} as const;

type TaxiOperationsText = { [Key in keyof typeof taxiOperationsEnglish]: string };

const taxiOperationsWords: Record<Locale, TaxiOperationsText> = {
  en: taxiOperationsEnglish,
  my: { ...taxiOperationsEnglish, drivers: "ယာဉ်မောင်းများ", orders: "အော်ဒါများ" },
  shn: { ...taxiOperationsEnglish, drivers: "ၽူႈႁေႃႈ", orders: "ၵၢၼ်သင်ႇ" },
  zh: { ...taxiOperationsEnglish, drivers: "司机", orders: "订单" },
};

const taxiStatusTone: Record<TaxiOrderStatus, "neutral" | "warning" | "success"> = {
  pending: "warning",
  confirmed: "neutral",
  assigned: "neutral",
  in_progress: "warning",
  completed: "success",
  cancelled: "neutral",
  rejected: "neutral",
};

function taxiStatusLabel(status: TaxiOrderStatus, locale: Locale) {
  const text = taxiOperationsWords[locale];
  return {
    pending: text.pending,
    confirmed: text.confirmed,
    assigned: text.assigned,
    in_progress: text.inProgress,
    completed: text.completed,
    cancelled: text.cancelled,
    rejected: text.rejected,
  }[status];
}

const taxiWords = {
  en: {
    directory: "Link Ride",
    directoryTitle: "Call a verified Taunggyi taxi",
    directoryHelp: "Call directly. TaungGyi Link does not automatically dispatch or track rides.",
    verifiedGroup: "Verified group",
    call: "Call",
    empty: "No verified taxi groups are published yet.",
    operations: "Link Ride operations",
    operationsTitle: "Group, drivers and records",
    createGroup: "Create taxi group",
    saveGroup: "Save group",
    published: "Published",
    waiting: "Waiting for Admin publishing approval",
    addDriver: "Add driver",
    addDriverAction: "Add driver",
    verified: "Verified",
    pending: "Pending verification",
    available: "Available",
    unavailable: "Unavailable",
    activity: "Manual trip records",
    activityHelp: "Record phone-dispatched trips for daily group reporting.",
    serviceDate: "Service date",
    pickup: "Pickup area",
    dropoff: "Drop-off area",
    fare: "Cash fare (MMK)",
    note: "Note",
    driver: "Driver",
    noDriver: "No driver selected",
    saveRecord: "Save trip record",
    recent: "Recent records",
    noRecords: "No trip records yet.",
    trips: "Trips",
    totalCash: "Recorded cash",
    saved: "Trip record saved.",
  },
  my: {
    directory: "တက္ကစီဖုန်းစာရင်း",
    directoryTitle: "တောင်ကြီးရှိ အတည်ပြုတက္ကစီကို ဖုန်းခေါ်ပါ",
    directoryHelp:
      "တိုက်ရိုက်ဖုန်းခေါ်ပါ။ TaungGyi Link မှ အလိုအလျောက်ခွဲဝေခြင်းနှင့် တည်နေရာခြေရာခံခြင်း မလုပ်ပါ။",
    verifiedGroup: "အတည်ပြုအဖွဲ့",
    call: "ဖုန်းခေါ်မည်",
    empty: "ထုတ်ပြန်ထားသော အတည်ပြုတက္ကစီအဖွဲ့ မရှိသေးပါ။",
    operations: "တက္ကစီစီမံခန့်ခွဲမှု",
    operationsTitle: "အဖွဲ့၊ ယာဉ်မောင်းနှင့် မှတ်တမ်းများ",
    createGroup: "တက္ကစီအဖွဲ့ ဖန်တီးရန်",
    saveGroup: "အဖွဲ့သိမ်းမည်",
    published: "ထုတ်ပြန်ထားသည်",
    waiting: "Admin ထုတ်ပြန်ခွင့်ကို စောင့်နေသည်",
    addDriver: "ယာဉ်မောင်းထည့်ရန်",
    addDriverAction: "ယာဉ်မောင်းထည့်မည်",
    verified: "အတည်ပြုပြီး",
    pending: "အတည်ပြုရန် စောင့်နေသည်",
    available: "အားလပ်",
    unavailable: "မအားလပ်",
    activity: "ကိုယ်တိုင်ခွဲဝေသည့် ခရီးမှတ်တမ်း",
    activityHelp: "ဖုန်းဖြင့်ခွဲဝေသောခရီးများကို နေ့စဉ်အစီရင်ခံရန် မှတ်တမ်းတင်ပါ။",
    serviceDate: "ဝန်ဆောင်သည့်ရက်",
    pickup: "ခေါ်ယူသည့်နေရာ",
    dropoff: "ပို့ဆောင်သည့်နေရာ",
    fare: "ငွေသားခ (MMK)",
    note: "မှတ်ချက်",
    driver: "ယာဉ်မောင်း",
    noDriver: "ယာဉ်မောင်း မရွေးထားပါ",
    saveRecord: "ခရီးမှတ်တမ်းသိမ်းမည်",
    recent: "လတ်တလောမှတ်တမ်းများ",
    noRecords: "ခရီးမှတ်တမ်း မရှိသေးပါ။",
    trips: "ခရီး",
    totalCash: "မှတ်တမ်းတင်ငွေ",
    saved: "ခရီးမှတ်တမ်း သိမ်းပြီးပါပြီ။",
  },
  shn: {
    directory: "သဵၼ်ႈမၢႆၾူၼ်းတႅၵ်ႇသီႇ",
    directoryTitle: "ႁွင်ႉတႅၵ်ႇသီႇဢၼ်ယိုၼ်ယၼ်ဝႆႉၼႂ်းတူၼ်ႈတီး",
    directoryHelp: "ႁွင်ႉၾူၼ်းသိုဝ်ႈ။ TaungGyi Link ဢမ်ႇၸတ်းလႄႈဢမ်ႇတိတ်းတၢမ်းတီႈယူႇ။",
    verifiedGroup: "ၸုမ်းယိုၼ်ယၼ်ယဝ်ႉ",
    call: "ႁွင်ႉၾူၼ်း",
    empty: "ပႆႇမီးၸုမ်းတႅၵ်ႇသီႇဢၼ်ပိုတ်ႇဝႆႉ။",
    operations: "ၸတ်းၵၢၼ်တႅၵ်ႇသီႇ",
    operationsTitle: "ၸုမ်း၊ ၽူႈႁေႃႈလႄႈမၢႆတွင်း",
    createGroup: "သၢင်ႈၸုမ်းတႅၵ်ႇသီႇ",
    saveGroup: "သိမ်းၸုမ်း",
    published: "ပိုတ်ႇဝႆႉ",
    waiting: "ပႂ်ႉ Admin ယိုၼ်ယၼ်",
    addDriver: "သႂ်ႇၽူႈႁေႃႈ",
    addDriverAction: "သႂ်ႇၽူႈႁေႃႈ",
    verified: "ယိုၼ်ယၼ်ယဝ်ႉ",
    pending: "ပႂ်ႉယိုၼ်ယၼ်",
    available: "တူဝ်ႈတၼ်း",
    unavailable: "ဢမ်ႇတူဝ်ႈတၼ်း",
    activity: "မၢႆတွင်းၶၢဝ်းတၢင်း",
    activityHelp: "မၢႆတွင်းၶၢဝ်းတၢင်းဢၼ်ၸတ်းတင်းၾူၼ်းတႃႇ report ဝၼ်း။",
    serviceDate: "ဝၼ်းႁဵတ်းၵၢၼ်",
    pickup: "တီႈႁပ်ႉ",
    dropoff: "တီႈသူင်ႇ",
    fare: "ၵႃႈၶၢဝ်းတၢင်း (MMK)",
    note: "မၢႆတွင်း",
    driver: "ၽူႈႁေႃႈ",
    noDriver: "ပႆႇလိူၵ်ႈၽူႈႁေႃႈ",
    saveRecord: "သိမ်းမၢႆတွင်း",
    recent: "မၢႆတွင်းလႃႈသုတ်း",
    noRecords: "ပႆႇမီးမၢႆတွင်း။",
    trips: "ၶၢဝ်းတၢင်း",
    totalCash: "ငိုၼ်းမၢႆတွင်း",
    saved: "သိမ်းမၢႆတွင်းယဝ်ႉ။",
  },
  zh: {
    directory: "出租车电话目录",
    directoryTitle: "致电东枝认证出租车",
    directoryHelp: "请直接致电。TaungGyi Link 不提供自动派单或位置追踪。",
    verifiedGroup: "认证车队",
    call: "致电",
    empty: "暂无已发布的认证出租车组。",
    operations: "出租车运营",
    operationsTitle: "车队、司机与记录",
    createGroup: "创建出租车组",
    saveGroup: "保存车队",
    published: "已发布",
    waiting: "等待管理员发布批准",
    addDriver: "添加司机",
    addDriverAction: "添加司机",
    verified: "已认证",
    pending: "待认证",
    available: "可接单",
    unavailable: "不可接单",
    activity: "人工派车记录",
    activityHelp: "记录电话派车行程，用于车队日报。",
    serviceDate: "服务日期",
    pickup: "上车区域",
    dropoff: "下车区域",
    fare: "现金车费（MMK）",
    note: "备注",
    driver: "司机",
    noDriver: "未选择司机",
    saveRecord: "保存行程记录",
    recent: "最近记录",
    noRecords: "暂无行程记录。",
    trips: "行程",
    totalCash: "记录现金",
    saved: "行程记录已保存。",
  },
} as const;

export function CustomerTaxiDirectory({
  locale,
  renderAuthPrompt,
  variant = "directory",
  limit,
  searchQuery = "",
  onResultCount,
}: {
  locale: Locale;
  renderAuthPrompt?: (onClose: () => void) => ReactNode;
  variant?: "directory" | "home";
  limit?: number;
  searchQuery?: string;
  onResultCount?: (count: number) => void;
}) {
  const a = useAuth(),
    text = taxiWords[locale],
    [groups, setGroups] = useState<Group[]>([]),
    [directoryError, setDirectoryError] = useFeedback("", "error"),
    [directoryLoaded, setDirectoryLoaded] = useState(false),
    [drivers, setDrivers] = useState<Driver[]>([]),
    [chosenGroup, setSelected] = useState<Group | null>(null),
    [loginRequestedFor, setLoginRequestedFor] = useState<string | null>(null),
    [requestMessage, setRequestMessage] = useFeedback();
  const [selectedDriver, setSelectedDriver] = useDetailRecord(drivers);
  const selected =
    chosenGroup ?? groups.find((group) => group.id === selectedDriver?.group_id) ?? null;
  useEffect(() => {
    if (!a.client) return;
    let on = true;
    void Promise.all([
      a.client
        .from("taxi_groups")
        .select(
          "id,organization_id,display_name,address,primary_phone,secondary_phone,service_note,is_published",
        ),
      a.client
        .from("taxi_drivers")
        .select(
          "id,group_id,user_id,display_name,phone,vehicle_type,vehicle_number,vehicle_color,is_available,is_verified,photo_data_url",
        ),
    ]).then(([g, d]) => {
      if (on) {
        setGroups((g.data ?? []) as Group[]);
        setDrivers(
          ((d.data ?? []) as Driver[]).filter(
            (driver) =>
              driver.is_verified &&
              driver.is_available &&
              driver.user_id &&
              ((g.data as Group[]) ?? []).some(
                (group) => group.id === driver.group_id && group.is_published,
              ),
          ),
        );
        setDirectoryError(
          g.error || d.error ? "Could not load taxi drivers. Please try again." : "",
        );
        setDirectoryLoaded(true);
      }
    });
    return () => {
      on = false;
    };
  }, [a.client, setDirectoryError]);
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const matchingDrivers = drivers.filter((driver) => {
    const group = groups.find((group) => group.id === driver.group_id && group.is_published);
    return (
      driver.is_verified &&
      driver.is_available &&
      Boolean(driver.user_id) &&
      group &&
      [
        driver.display_name,
        driver.vehicle_color,
        driver.vehicle_type,
        driver.vehicle_number,
        group.address,
        group.display_name,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(normalizedSearch)
    );
  });
  const visibleDrivers =
    typeof limit === "number" ? matchingDrivers.slice(0, limit) : matchingDrivers;
  useEffect(() => onResultCount?.(matchingDrivers.length), [matchingDrivers.length, onResultCount]);
  const checkoutId = useRef<string | null>(null);
  const checkoutLock = useRef(false);
  async function submitRideOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!a.client || !a.user || !selected) return;
    if (checkoutLock.current) return;
    checkoutLock.current = true;
    checkoutId.current ??= crypto.randomUUID();
    const form = event.currentTarget;
    const data = new FormData(form);
    const requestedPickup = String(data.get("requestedPickup") ?? "");
    try {
      const { error } = await submitOrderRequest(a.client, "taxi_orders", checkoutId.current, {
        group_id: selected.id,
        customer_user_id: a.user.id,
        driver_id: selectedDriver?.id ?? null,
        source: "customer",
        status: "pending",
        customer_name: data.get("customerName"),
        customer_phone: data.get("customerPhone"),
        pickup_area: data.get("pickup"),
        dropoff_area: data.get("dropoff"),
        requested_pickup_at: requestedPickup || null,
        fare_minor: 0,
        note: data.get("note"),
        created_by: a.user.id,
      });
      setRequestMessage(error?.message ?? taxiOperationsWords[locale].orderSent);
      if (!error) {
        form.reset();
        checkoutId.current = null;
        setSelected(null);
      }
    } catch (error) {
      setRequestMessage(
        error instanceof Error ? error.message : "Connection lost. Retry this request.",
      );
    } finally {
      checkoutLock.current = false;
    }
  }
  if (normalizedSearch && !matchingDrivers.length && !directoryError) return null;
  return (
    <section className={`taxi-feature ${variant === "home" ? "provider-home-directory" : ""}`}>
      {variant === "directory" && (
        <div className="section-heading">
          <div>
            <h2>{text.directoryTitle}</h2>
          </div>
        </div>
      )}
      {directoryError && <p role="alert">{directoryError}</p>}
      {!directoryLoaded && <p role="status">Loading taxi drivers…</p>}
      <div className="hotel-directory-grid" hidden={Boolean(selected)}>
        <PaginatedList
          layout="cards"
          items={visibleDrivers}
          resetKey={normalizedSearch}
          label="Taxi driver pages"
          renderItem={(driver) => (
            <button
              className="hotel-listing-card"
              key={driver.id}
              type="button"

              onClick={() => {
                setSelected(groups.find((group) => group.id === driver.group_id) ?? null);
                setSelectedDriver(driver);
                setRequestMessage("");
                setLoginRequestedFor(null);
              }}
            >
              <TaxiDriverArtwork key={driver.id} driver={driver} />
              <span className="hotel-listing-card__body">
                <strong>{driver.display_name}</strong>
                <small>
                  {driver.vehicle_color || "Color not provided"} · {driver.vehicle_number}
                </small>
                <small>{groups.find((group) => group.id === driver.group_id)?.address}</small>
                <b>
                  {driver.is_available
                    ? "Verified driver · Available"
                    : "Verified driver · Unavailable"}
                </b>
                <em>
                  {text.call} <span aria-hidden="true">→</span>
                </em>
              </span>
            </button>
          )}
        />
        {directoryLoaded && !directoryError && !visibleDrivers.length && !normalizedSearch && (
          <Card className="empty-state">
            <strong>No verified taxi drivers are available yet.</strong>
          </Card>
        )}
      </div>
      {selected && selectedDriver && drivers.some((d) => d.id === selectedDriver.id) && (
        <div className="provider-detail-page" role="presentation">
          <section
            className="provider-detail-content"
            role="region"
            aria-labelledby="taxi-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="hotel-detail-dialog__content">
              <div className="taxi-driver-profile">
                <ProviderSaveButton org={selected.organization_id} />
                <TaxiDriverArtwork driver={selectedDriver ?? undefined} />
                <div className="taxi-driver-profile__info">
                  <StatusPill tone={selectedDriver?.is_available ? "success" : "neutral"}>
                    {selectedDriver?.is_available ? "Available" : "Unavailable"}
                  </StatusPill>
                  <h3 id="taxi-detail-title">{selectedDriver?.display_name}</h3>
                  <dl>
                    <div>
                      <dt>Car color</dt>
                      <dd>{selectedDriver?.vehicle_color || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Vehicle number</dt>
                      <dd>{selectedDriver?.vehicle_number}</dd>
                    </div>
                  </dl>
                  {a.user && selectedDriver ? (
                    <TaxiCallButton
                      driverId={selectedDriver.id}
                      disabled={!selectedDriver.is_available || !selectedDriver.user_id}
                    />
                  ) : (
                    <button className="button" onClick={() => setLoginRequestedFor(selected.id)}>
                      Sign in to call
                    </button>
                  )}
                </div>
              </div>
              <p className="ride-fare-notice" role="note">
                {rideFareNotice[locale]}
              </p>
              {a.user && selectedDriver?.is_available && selectedDriver.user_id && (
                <RecordFormDialog
                  title={taxiOperationsWords[locale].requestRide}
                  triggerLabel="Request a ride"
                >
                  <form
                    className="form-grid form-grid--two"
                    onSubmit={(event) => void submitRideOrder(event)}
                  >
                    <label className="field">
                      <span>{taxiOperationsWords[locale].customerName}</span>
                      <input
                        name="customerName"
                        defaultValue={String(a.user.user_metadata.display_name ?? "")}
                        required
                      />
                    </label>
                    <label className="field">
                      <span>{taxiOperationsWords[locale].customerPhone}</span>
                      <input name="customerPhone" type="tel" required />
                    </label>
                    <label className="field">
                      <span>{text.pickup}</span>
                      <input name="pickup" maxLength={160} required />
                    </label>
                    <label className="field">
                      <span>{text.dropoff}</span>
                      <input name="dropoff" maxLength={160} required />
                    </label>
                    <label className="field">
                      <span>{taxiOperationsWords[locale].requestedPickup}</span>
                      <input name="requestedPickup" type="datetime-local" />
                    </label>
                    <label className="field">
                      <span>{text.note}</span>
                      <input name="note" maxLength={1000} />
                    </label>
                    <button className="button field--wide">
                      {taxiOperationsWords[locale].sendOrder}
                    </button>
                  </form>
                  {requestMessage && <p className="form-message">{requestMessage}</p>}
                </RecordFormDialog>
              )}
              <ProviderReputation
                key={selectedDriver.id}
                org={selected.organization_id}
                driverId={selectedDriver.id}
                hideSave
              />
              {loginRequestedFor === selected.id &&
                !a.user &&
                renderAuthPrompt?.(() => setLoginRequestedFor(null))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function TaxiDriverArtwork({ driver }: { driver?: Driver }) {
  const [failed, setFailed] = useState(false);
  if (!driver?.photo_data_url || failed)
    return (
      <span
        className="hotel-listing-card__image driver-photo-placeholder"
        aria-label="Driver photo not provided"
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 22v-2a8 8 0 0 1 16 0v2" />
        </svg>
      </span>
    );
  return (
    <img
      className="hotel-listing-card__image taxi-listing-photo"
      src={driver.photo_data_url}
      alt={`${driver.display_name} portrait`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function useTaxiWorkspace() {
  const auth = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [orders, setOrders] = useState<TaxiOrder[]>([]);

  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data: membership } = await auth.client
      .from("organization_memberships")
      .select("organization_id")
      .eq("user_id", auth.user.id)
      .eq("role", "taxi_group_leader")
      .eq("status", "active")
      .limit(1)
      .maybeSingle();
    if (!membership) return;
    setOrganizationId(membership.organization_id);
    const { data: groupData } = await auth.client
      .from("taxi_groups")
      .select(
        "id,organization_id,display_name,address,primary_phone,secondary_phone,service_note,is_published",
      )
      .eq("organization_id", membership.organization_id)
      .maybeSingle();
    const nextGroup = (groupData as Group | null) ?? null;
    setGroup(nextGroup);
    if (!nextGroup) {
      setDrivers([]);
      setOrders([]);
      return;
    }
    const [driverResult, profileResult, orderResult] = await Promise.all([
      auth.client
        .from("taxi_drivers")
        .select(
          "id,group_id,user_id,display_name,phone,vehicle_type,vehicle_number,vehicle_color,is_available,is_verified,photo_data_url",
        )
        .eq("group_id", nextGroup.id),
      auth.client
        .from("taxi_driver_profiles")
        .select("driver_id,group_id,nrc")
        .eq("group_id", nextGroup.id),
      auth.client
        .from("taxi_orders")
        .select(
          "id,group_id,customer_user_id,driver_id,source,status,customer_name,customer_phone,pickup_area,dropoff_area,requested_pickup_at,fare_minor,note,cancellation_reason,created_by,created_at",
        )
        .eq("group_id", nextGroup.id)
        .order("created_at", { ascending: false })
        .limit(500),
    ]);
    const profiles = (profileResult.data ?? []) as DriverProfile[];
    const nrcByDriver = new Map(profiles.map((profile) => [profile.driver_id, profile.nrc]));
    setDrivers(
      ((driverResult.data ?? []) as Driver[]).map((driver) => ({
        ...driver,
        nrc: nrcByDriver.get(driver.id) ?? "",
      })),
    );
    setOrders((orderResult.data ?? []) as TaxiOrder[]);
  }, [auth.client, auth.user]);

  useOrderRefresh(load);
  useEffect(() => {
    const timeout = setTimeout(() => void load(), 0);
    return () => clearTimeout(timeout);
  }, [load]);

  return { auth, organizationId, group, drivers, orders, load };
}

function OrderSummary({
  order,
  drivers,
  locale,
}: {
  order: TaxiOrder;
  drivers: Driver[];
  locale: Locale;
}) {
  const { user } = useAuth();
  const driver = drivers.find((candidate) => candidate.id === order.driver_id);
  const ownRecord = order.source === "manual" && order.created_by === user?.id;
  const words = taxiOperationsWords[locale];
  return (
    <div className="taxi-order-summary">
      <div className="taxi-order-summary__topline">
        <StatusPill tone={taxiStatusTone[order.status]}>
          {taxiStatusLabel(order.status, locale)}
        </StatusPill>
        <small>{formatRecordDate(order.created_at, locale, true)}</small>
      </div>
      <strong>{order.customer_name}</strong>
      {order.cancellation_reason && (
        <p>
          <strong>Cancellation reason:</strong> {order.cancellation_reason}
        </p>
      )}
      <span>
        {order.pickup_area} → {order.dropoff_area}
      </span>
      <small>
        {driver?.display_name ??
          (ownRecord ? String(user?.user_metadata.display_name ?? "You") : words.unassigned)}{" "}
        · {order.source === "customer" ? words.customerOrder : words.manualRecord}
      </small>
    </div>
  );
}

export function TaxiDashboard({ locale }: { locale: Locale }) {
  const { group, drivers, orders } = useTaxiWorkspace();
  const words = taxiOperationsWords[locale];
  if (!group) return <AssignedTaxiOrders />;
  const metrics = [
    {
      label: words.pendingOrders,
      count: orders.filter((order) => order.status === "pending").length,
      tone: "warning" as const,
    },
    {
      label: words.assignedOrders,
      count: orders.filter((order) => order.status === "assigned" || order.status === "confirmed")
        .length,
      tone: "neutral" as const,
    },
    {
      label: words.activeOrders,
      count: orders.filter((order) => order.status === "in_progress").length,
      tone: "warning" as const,
    },
    {
      label: words.completedOrders,
      count: orders.filter((order) => order.status === "completed").length,
      tone: "success" as const,
    },
  ];
  const recentCustomerOrders = orders.filter((order) => order.source === "customer").slice(0, 5);
  return (
    <section className="taxi-feature taxi-dashboard">
      <div className="operations-grid taxi-dashboard__metrics">
        {metrics.map((metric) => (
          <Card className="metric-card" key={metric.label}>
            <StatusPill tone={metric.tone}>{metric.label}</StatusPill>
            <strong>{metric.count}</strong>
            <span>{metric.label}</span>
          </Card>
        ))}
      </div>
      <div className="taxi-dashboard__recent">
        <Card className="management-card taxi-summary-card">
          <div className="taxi-card-heading">
            <h3>{words.recentOrders}</h3>
            <a className="home-show-more" href="/orders?source=customer">
              {words.seeMore}
            </a>
          </div>
          <RecordTable
            label={words.recentOrders}
            items={recentCustomerOrders}
            rowKey={(order) => order.id}
            empty={words.noOrders}
            columns={[
              {
                key: "customer",
                label: words.customerName,
                render: (order) => (
                  <>
                    <strong className="record-table__name">{order.customer_name}</strong>
                    <div className="record-table__secondary">
                      {formatRecordDate(order.created_at, locale, true)}
                    </div>
                  </>
                ),
              },
              {
                key: "route",
                label: `${taxiWords[locale].pickup} → ${taxiWords[locale].dropoff}`,
                render: (order) => (
                  <>
                    <span>
                      {order.pickup_area} → {order.dropoff_area}
                    </span>
                  </>
                ),
              },
              {
                key: "driver",
                label: words.drivers,
                render: (order) =>
                  drivers.find((driver) => driver.id === order.driver_id)?.display_name ??
                  words.unassigned,
              },
              {
                key: "status",
                label: words.status,
                render: (order) => (
                  <StatusPill tone={taxiStatusTone[order.status]}>
                    {taxiStatusLabel(order.status, locale)}
                  </StatusPill>
                ),
              },
            ]}
          />
        </Card>
      </div>
    </section>
  );
}

export function TaxiDrivers({ locale }: { locale: Locale }) {
  const { auth, organizationId, group, drivers, load } = useTaxiWorkspace();
  const text = taxiWords[locale];
  const words = taxiOperationsWords[locale];
  const [query, setQuery] = useState("");
  const [verification, setVerification] = useState<"all" | "verified" | "pending">("all");
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoError, setPhotoError] = useFeedback("", "error");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const photoSelection = useRef(0);

  async function selectPhoto(file: File | undefined) {
    if (!file) return;
    const selection = ++photoSelection.current;
    setPhotoError("");
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setPhotoError("Choose a JPEG, PNG, or WebP photo up to 5 MB.");
      return;
    }
    setPhotoBusy(true);
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      const scale = Math.min(1, 640 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not prepare this photo.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const prepared = canvas.toDataURL("image/jpeg", 0.8);
      if (prepared.length > 524288) throw new Error("Please choose a smaller photo.");
      if (selection === photoSelection.current) setPhoto(prepared);
    } catch (error) {
      if (selection === photoSelection.current)
        setPhotoError(error instanceof Error ? error.message : "Could not load the photo.");
    } finally {
      URL.revokeObjectURL(url);
      if (selection === photoSelection.current) setPhotoBusy(false);
    }
  }

  const [message, setMessage] = useFeedback();

  function closeEditor() {
    if (saving) return;
    photoSelection.current += 1;
    setPhotoBusy(false);
    setEditorOpen(false);
    setEditingDriver(null);
  }

  function openNewDriver() {
    setPhoto(null);
    setPhotoError("");
    setMessage("");
    setEditingDriver(null);
    setEditorOpen(true);
  }

  function openDriverEditor(driver: Driver) {
    setPhoto(driver.photo_data_url ?? null);
    setPhotoError("");
    setMessage("");
    setEditingDriver(driver);
    setEditorOpen(true);
  }

  const filteredDrivers = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return drivers.filter(
      (driver) =>
        (verification === "all" || driver.is_verified === (verification === "verified")) &&
        (!normalized ||
          [driver.display_name, driver.nrc ?? ""].some((value) =>
            value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [drivers, query, verification]);

  async function createGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || !organizationId) return;
    const data = new FormData(event.currentTarget);
    const { error } = await auth.client.from("taxi_groups").insert({
      organization_id: organizationId,
      display_name: data.get("name"),
      address: data.get("address"),
      primary_phone: data.get("phone"),
      service_note: data.get("note"),
    });
    setMessage(error?.message ?? "Link Ride group saved.");
    if (!error) await load();
  }

  async function saveDriver(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || !group || photoBusy || saving) return;
    setSaving(true);
    try {
      const form = event.currentTarget;
      const data = new FormData(form);
      const values = {
        display_name: data.get("name"),
        photo_data_url: photo,
        phone: data.get("phone"),
        vehicle_type: data.get("vehicle"),
        vehicle_number: data.get("number"),
        is_available: data.get("available") === "on",
      };
      if (editingDriver) {
        const driverResult = await auth.client
          .from("taxi_drivers")
          .update(values)
          .eq("id", editingDriver.id);
        if (driverResult.error) {
          setMessage(driverResult.error.message);
          return;
        }
        const profileResult = editingDriver.nrc
          ? await auth.client
              .from("taxi_driver_profiles")
              .update({ nrc: data.get("nrc") })
              .eq("driver_id", editingDriver.id)
          : await auth.client
              .from("taxi_driver_profiles")
              .insert({ driver_id: editingDriver.id, group_id: group.id, nrc: data.get("nrc") });
        setMessage(profileResult.error?.message ?? words.driverUpdated);
        if (!profileResult.error) {
          setEditorOpen(false);
          setEditingDriver(null);
        }
      } else {
        const driverResult = await auth.client
          .from("taxi_drivers")
          .insert({ group_id: group.id, ...values })
          .maybeSingle();
        if (driverResult.error || !driverResult.data) {
          setMessage(driverResult.error?.message ?? "Driver could not be created.");
          return;
        }
        const profileResult = await auth.client.from("taxi_driver_profiles").insert({
          driver_id: driverResult.data.id,
          group_id: group.id,
          nrc: data.get("nrc"),
        });
        setMessage(profileResult.error?.message ?? words.driverAdded);
        if (!profileResult.error) {
          setEditorOpen(false);
          setEditingDriver(null);
        }
      }
      form.reset();
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function toggleDriver(driver: Driver) {
    if (!auth.client) return;
    const { error } = await auth.client
      .from("taxi_drivers")
      .update({ is_available: !driver.is_available })
      .eq("id", driver.id);
    setMessage(error?.message ?? (driver.is_available ? text.unavailable : text.available));
    if (!error) await load();
  }

  async function deleteDriver(driver: Driver) {
    if (!auth.client || !window.confirm(`Delete ${driver.display_name}?`)) return;
    const { error } = await auth.client.from("taxi_drivers").delete().eq("id", driver.id);
    setMessage(error?.message ?? words.driverDeleted);
    if (!error) {
      if (editingDriver?.id === driver.id) closeEditor();
      await load();
    }
  }

  return (
    <section className="taxi-feature taxi-list-page">
      {!group && organizationId && (
        <RecordFormDialog title={text.createGroup}>
          <form className="form-grid" onSubmit={(event) => void createGroup(event)}>
            {["name", "address", "phone", "note"].map((field) => (
              <label className="field" key={field}>
                <span>{field}</span>
                <input name={field} required={field !== "note"} />
              </label>
            ))}
            <button className="button">{text.saveGroup}</button>
          </form>
        </RecordFormDialog>
      )}
      {group && (
        <>
          <div className="taxi-list-page-heading">
            <h1>{words.drivers}</h1>
            <button className="button" type="button" onClick={openNewDriver}>
              + {text.addDriver}
            </button>
          </div>
          <ManagementListToolbar
            query={query}
            onQueryChange={setQuery}
            searchLabel={words.searchDrivers}
            resultCount={filteredDrivers.length}
            filters={[
              {
                label: "Verification",
                value: verification,
                onChange: (value) => setVerification(value as typeof verification),
                options: [
                  { value: "all", label: words.allDrivers },
                  { value: "verified", label: text.verified },
                  { value: "pending", label: text.pending },
                ],
              },
            ]}
            onClear={() => {
              setQuery("");
              setVerification("all");
            }}
          />
          <div className="request-list">
            <PaginatedList
              items={filteredDrivers}
              resetKey={`${query}:${verification}`}
              label="Taxi driver pages"
              renderItem={(driver) => (
                <Card className="request-card taxi-driver-card" key={driver.id}>
                  <div>
                    {driver.photo_data_url && (
                      <img
                        className="taxi-driver-photo"
                        src={driver.photo_data_url}
                        alt={`${driver.display_name} portrait`}
                      />
                    )}
                    <StatusPill tone={driver.is_verified ? "success" : "warning"}>
                      {driver.is_verified ? text.verified : text.pending}
                    </StatusPill>
                    <h3>{driver.display_name}</h3>
                    <p>
                      {words.nrc}: {driver.nrc || "—"}
                    </p>
                    <p>
                      {driver.vehicle_type} · {driver.vehicle_number}
                    </p>
                    <a href={`tel:${driver.phone}`}>{driver.phone}</a>
                  </div>
                  <div className="request-actions taxi-card-actions">
                    <button
                      className="text-button action-capsule action-capsule--state"
                      type="button"
                      onClick={() => void toggleDriver(driver)}
                    >
                      {driver.is_available ? text.unavailable : text.available}
                    </button>
                    <button
                      className="text-button action-capsule action-capsule--edit"
                      type="button"
                      onClick={() => openDriverEditor(driver)}
                    >
                      {words.edit}
                    </button>
                    <button
                      className="text-button text-button--danger action-capsule action-capsule--delete"
                      type="button"
                      onClick={() => void deleteDriver(driver)}
                    >
                      {words.delete}
                    </button>
                  </div>
                </Card>
              )}
            />
          </div>
          {editorOpen && (
            <div className="finance-dialog-backdrop" role="presentation" onMouseDown={closeEditor}>
              <section
                className="finance-dialog taxi-editor-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="taxi-driver-editor-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <header className="finance-dialog__header">
                  <div>
                    <p className="eyebrow">{words.drivers}</p>
                    <h2 id="taxi-driver-editor-title">
                      {editingDriver ? words.saveChanges : text.addDriver}
                    </h2>
                  </div>
                  <button
                    className="icon-button"
                    type="button"
                    aria-label={words.cancel}
                    onClick={closeEditor}
                  >
                    ×
                  </button>
                </header>
                <form
                  key={editingDriver?.id ?? "new-driver"}
                  className="form-grid form-grid--two finance-dialog__form"
                  onSubmit={(event) => void saveDriver(event)}
                >
                  <div className="field field--wide taxi-driver-photo-editor">
                    {photo && (
                      <img className="taxi-driver-photo" src={photo} alt="Driver photo preview" />
                    )}
                    <label className="field">
                      <span>Driver photo (optional)</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={saving || photoBusy}
                        onChange={(event) => {
                          void selectPhoto(event.target.files?.[0]);
                          event.target.value = "";
                        }}
                      />
                      <small>JPEG, PNG, or WebP · up to 5 MB</small>
                    </label>
                    {photo && (
                      <button
                        className="text-button"
                        type="button"
                        disabled={saving || photoBusy}
                        onClick={() => {
                          setPhoto(null);
                          setPhotoError("");
                        }}
                      >
                        Remove photo
                      </button>
                    )}
                    {photoBusy && <small role="status">Preparing photo…</small>}
                    {photoError && (
                      <p className="form-message" role="alert">
                        {photoError}
                      </p>
                    )}
                  </div>
                  <label className="field">
                    <span>{words.drivers}</span>
                    <input name="name" defaultValue={editingDriver?.display_name} required />
                  </label>
                  <label className="field">
                    <span>{words.nrc}</span>
                    <input name="nrc" defaultValue={editingDriver?.nrc} required />
                  </label>
                  <label className="field">
                    <span>Phone</span>
                    <input name="phone" type="tel" defaultValue={editingDriver?.phone} required />
                  </label>
                  <label className="field">
                    <span>Vehicle</span>
                    <input name="vehicle" defaultValue={editingDriver?.vehicle_type} required />
                  </label>
                  <label className="field">
                    <span>Vehicle number</span>
                    <input name="number" defaultValue={editingDriver?.vehicle_number} required />
                  </label>
                  <label className="taxi-checkbox">
                    <input
                      name="available"
                      type="checkbox"
                      defaultChecked={editingDriver?.is_available ?? true}
                    />
                    <span>{text.available}</span>
                  </label>
                  {message && (
                    <p className="form-message field--wide" role="status">
                      {message}
                    </p>
                  )}
                  <div className="taxi-form-actions field--wide">
                    <button className="button" disabled={saving || photoBusy}>
                      {editingDriver ? words.saveChanges : text.addDriverAction}
                    </button>
                    <button className="text-button" type="button" onClick={closeEditor}>
                      {words.cancel}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          )}
        </>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

export function TaxiOrders({ locale }: { locale: Locale }) {
  const { auth, group, drivers, orders, load } = useTaxiWorkspace();
  const text = taxiWords[locale];
  const words = taxiOperationsWords[locale];
  const [query, setQuery] = useState("");
  const [driverFilter, setDriverFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(() => {
    const requestedSource = new URLSearchParams(window.location.search).get("source");
    return requestedSource === "customer" || requestedSource === "manual" ? requestedSource : "all";
  });
  const filterDialog = useRef<HTMLDialogElement>(null);
  const [draftFilters, setDraftFilters] = useState({
    query: "",
    source: "all",
    driver: "all",
    status: "all",
  });
  const [editingOrder, setEditingOrder] = useState<TaxiOrder | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [message, setMessage] = useFeedback();
  const cancelDialog = useRef<HTMLDialogElement>(null);
  const [cancelTarget, setCancelTarget] = useState<TaxiOrder | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [responseError, setResponseError] = useFeedback("", "error");
  const [responding, setResponding] = useState(false);

  function closeEditor() {
    setEditorOpen(false);
    setEditingOrder(null);
  }

  function openNewOrder() {
    setEditingOrder(null);
    setEditorOpen(true);
  }

  const statuses: TaxiOrderStatus[] = [
    "pending",
    "confirmed",
    "assigned",
    "in_progress",
    "completed",
    "cancelled",
    "rejected",
  ];

  const filteredOrders = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return orders.filter(
      (order) =>
        (driverFilter === "all" ||
          (driverFilter === "unassigned" ? !order.driver_id : order.driver_id === driverFilter)) &&
        (statusFilter === "all" || order.status === statusFilter) &&
        (sourceFilter === "all" || order.source === sourceFilter) &&
        (!normalized ||
          [order.customer_name, order.customer_phone, order.pickup_area, order.dropoff_area].some(
            (value) => value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [driverFilter, orders, query, sourceFilter, statusFilter]);

  async function saveOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || !auth.user || !group) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const driverId = drivers.find((driver) => driver.user_id === auth.user?.id)?.id ?? null;
    const requestedPickup = String(data.get("requestedPickup") ?? "");
    const values = {
      driver_id: driverId,
      status: data.get("status"),
      customer_name: data.get("customerName"),
      customer_phone: data.get("customerPhone"),
      pickup_area: data.get("pickup"),
      dropoff_area: data.get("dropoff"),
      requested_pickup_at: requestedPickup || null,
      fare_minor: Math.round(Number(data.get("fare") || 0) * 100),
      note: data.get("note"),
    };
    const result = editingOrder
      ? await auth.client.from("taxi_orders").update(values).eq("id", editingOrder.id)
      : await auth.client.from("taxi_orders").insert({
          group_id: group.id,
          customer_user_id: null,
          source: "manual",
          ...values,
          created_by: auth.user.id,
        });
    setMessage(result.error?.message ?? (editingOrder ? words.orderUpdated : words.orderAdded));
    if (!result.error) {
      closeEditor();
      form.reset();
      await load();
    }
  }

  async function respondToOrder(order: TaxiOrder, response: "confirmed" | "cancelled") {
    if (!auth.client || responding) return;
    setResponding(true);
    setResponseError("");
    setMessage("");
    const result = await auth.client.rpc("respond_to_taxi_order", {
      target_order: order.id,
      response,
      reason: response === "cancelled" ? cancelReason.trim() : "",
    });
    setResponding(false);
    if (result.error) {
      if (response === "cancelled") setResponseError(result.error.message);
      else setMessage(result.error.message);
      return;
    }
    cancelDialog.current?.close();
    setMessage(response === "confirmed" ? "Order confirmed." : "Order cancelled.");
    await load();
  }

  if (!group) return <AssignedTaxiOrders />;
  return (
    <section className="taxi-feature taxi-list-page">
      {group && (
        <>
          <div className="taxi-list-page-heading taxi-orders-heading">
            <h1>{words.orders}</h1>
            <div className="taxi-orders-heading__actions">
              <button
                className="toolbar-icon-button"
                type="button"
                aria-label={words.addRecord}
                title={words.addRecord}
                onClick={openNewOrder}
              >
                <ActionIcon action="add" />
              </button>
              <button
                className="toolbar-icon-button"
                type="button"
                aria-label="Filter orders"
                title="Filter orders"
                aria-haspopup="dialog"
                data-filtered={Boolean(
                  query.trim() ||
                  sourceFilter !== "all" ||
                  driverFilter !== "all" ||
                  statusFilter !== "all",
                )}
                onClick={() => {
                  setDraftFilters({
                    query,
                    source: sourceFilter,
                    driver: driverFilter,
                    status: statusFilter,
                  });
                  filterDialog.current?.showModal();
                }}
              >
                <ActionIcon action="filter" />
              </button>
            </div>
          </div>
          <dialog
            ref={filterDialog}
            className="customer-order-filter-dialog"
            aria-labelledby="taxi-order-filter-title"
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setQuery(draftFilters.query);
                setSourceFilter(draftFilters.source);
                setDriverFilter(draftFilters.driver);
                setStatusFilter(draftFilters.status);
                filterDialog.current?.close();
              }}
            >
              <header>
                <h2 id="taxi-order-filter-title">Filter orders</h2>
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Close filters"
                  onClick={() => filterDialog.current?.close()}
                >
                  ×
                </button>
              </header>
              <label className="field">
                <span>Search</span>
                <input
                  type="search"
                  aria-label={words.searchOrders}
                  placeholder={words.searchOrders}
                  value={draftFilters.query}
                  onChange={(event) =>
                    setDraftFilters({ ...draftFilters, query: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>{words.source}</span>
                <select
                  aria-label={words.source}
                  value={draftFilters.source}
                  onChange={(event) =>
                    setDraftFilters({ ...draftFilters, source: event.target.value })
                  }
                >
                  <option value="all">{words.allSources}</option>
                  <option value="customer">{words.customerOrder}</option>
                  <option value="manual">{words.manualRecord}</option>
                </select>
              </label>
              <label className="field">
                <span>{text.driver}</span>
                <select
                  aria-label={text.driver}
                  value={draftFilters.driver}
                  onChange={(event) =>
                    setDraftFilters({ ...draftFilters, driver: event.target.value })
                  }
                >
                  <option value="all">{words.allDriversFilter}</option>
                  <option value="unassigned">{words.unassigned}</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{words.status}</span>
                <select
                  aria-label={words.status}
                  value={draftFilters.status}
                  onChange={(event) =>
                    setDraftFilters({ ...draftFilters, status: event.target.value })
                  }
                >
                  <option value="all">{words.allStatuses}</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {taxiStatusLabel(status, locale)}
                    </option>
                  ))}
                </select>
              </label>
              <footer>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() =>
                    setDraftFilters({ query: "", source: "all", driver: "all", status: "all" })
                  }
                >
                  Reset
                </button>
                <button className="button" type="submit">
                  Apply filters
                </button>
              </footer>
            </form>
          </dialog>
          <div className="request-list">
            <PaginatedList
              items={filteredOrders}
              resetKey={`${query}:${sourceFilter}:${driverFilter}:${statusFilter}`}
              label="Taxi order pages"
              renderItem={(order) => (
                <Card className="request-card taxi-order-card" key={order.id}>
                  <OrderSummary order={order} drivers={drivers} locale={locale} />
                  <div className="request-actions taxi-card-actions">
                    {order.status === "pending" && (
                      <button
                        className="text-button action-capsule action-capsule--state"
                        type="button"
                        disabled={responding}
                        onClick={() => void respondToOrder(order, "confirmed")}
                      >
                        Confirm
                      </button>
                    )}
                    {(["pending", "confirmed", "assigned", "in_progress"] as string[]).includes(
                      order.status,
                    ) && (
                      <button
                        className="text-button text-button--danger action-capsule action-capsule--delete"
                        type="button"
                        disabled={responding}
                        onClick={() => {
                          setCancelTarget(order);
                          setCancelReason("");
                          setResponseError("");
                          cancelDialog.current?.showModal();
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </Card>
              )}
            />
            {!filteredOrders.length && (
              <Card className="empty-state">
                <strong>{words.noOrders}</strong>
              </Card>
            )}
          </div>
          <dialog
            ref={cancelDialog}
            className="customer-order-filter-dialog"
            aria-labelledby="taxi-cancel-title"
            onCancel={(event) => {
              if (responding) event.preventDefault();
            }}
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (cancelTarget && cancelReason.trim())
                  void respondToOrder(cancelTarget, "cancelled");
              }}
            >
              <header>
                <h2 id="taxi-cancel-title">Cancel order</h2>
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Close cancellation"
                  disabled={responding}
                  onClick={() => cancelDialog.current?.close()}
                >
                  ×
                </button>
              </header>
              <p>
                {cancelTarget?.customer_name} · {cancelTarget?.pickup_area} →{" "}
                {cancelTarget?.dropoff_area}
              </p>
              <label className="field">
                <span>Cancellation reason</span>
                <textarea
                  aria-label="Cancellation reason"
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                  required
                  maxLength={1000}
                  rows={4}
                  disabled={responding}
                  placeholder="Explain why this ride cannot go ahead…"
                />
              </label>
              <small>This reason will be shown to the customer.</small>
              {responseError && (
                <p role="alert" className="form-message">
                  {responseError}
                </p>
              )}
              <footer>
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={responding}
                  onClick={() => cancelDialog.current?.close()}
                >
                  Keep order
                </button>
                <button
                  className="button button--danger"
                  type="submit"
                  disabled={responding || !cancelReason.trim()}
                >
                  {responding ? "Cancelling…" : "Cancel order"}
                </button>
              </footer>
            </form>
          </dialog>
          {editorOpen && (
            <div className="finance-dialog-backdrop" role="presentation" onMouseDown={closeEditor}>
              <section
                className="finance-dialog taxi-editor-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="taxi-order-editor-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <header className="finance-dialog__header">
                  <div>
                    <p className="eyebrow">{words.orders}</p>
                    <h2 id="taxi-order-editor-title">
                      {editingOrder ? words.updateRecord : words.addRecord}
                    </h2>
                  </div>
                  <button
                    className="icon-button"
                    type="button"
                    aria-label={words.cancel}
                    onClick={closeEditor}
                  >
                    ×
                  </button>
                </header>
                <form
                  key={editingOrder?.id ?? "new-order"}
                  className="form-grid form-grid--two finance-dialog__form"
                  onSubmit={(event) => void saveOrder(event)}
                >
                  <label className="field">
                    <span>{words.customerName}</span>
                    <input
                      name="customerName"
                      defaultValue={editingOrder?.customer_name}
                      required
                    />
                  </label>
                  <label className="field">
                    <span>{words.customerPhone}</span>
                    <input
                      name="customerPhone"
                      type="tel"
                      defaultValue={editingOrder?.customer_phone}
                      required
                    />
                  </label>
                  <label className="field">
                    <span>{text.pickup}</span>
                    <input name="pickup" defaultValue={editingOrder?.pickup_area} required />
                  </label>
                  <label className="field">
                    <span>{text.dropoff}</span>
                    <input name="dropoff" defaultValue={editingOrder?.dropoff_area} required />
                  </label>
                  <label className="field">
                    <span>{words.requestedPickup}</span>
                    <input
                      name="requestedPickup"
                      type="datetime-local"
                      defaultValue={editingOrder?.requested_pickup_at?.slice(0, 16)}
                    />
                  </label>
                  <label className="field">
                    <span>{words.status}</span>
                    <select name="status" defaultValue={editingOrder?.status ?? "completed"}>
                      {statuses.map((status) => (
                        <option value={status} key={status}>
                          {taxiStatusLabel(status, locale)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>{text.fare}</span>
                    <input
                      name="fare"
                      type="number"
                      min="0"
                      step="100"
                      defaultValue={editingOrder ? editingOrder.fare_minor / 100 : 0}
                      required
                    />
                  </label>
                  <label className="field field--wide">
                    <span>{text.note}</span>
                    <textarea name="note" maxLength={1000} defaultValue={editingOrder?.note} />
                  </label>
                  <div className="taxi-form-actions field--wide">
                    <button className="button">
                      {editingOrder ? words.updateRecord : words.addRecord}
                    </button>
                    <button className="text-button" type="button" onClick={closeEditor}>
                      {words.cancel}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          )}
        </>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

export function TaxiLeaderConsole({ locale }: { locale: Locale }) {
  return <TaxiDrivers locale={locale} />;
}
